<template>
  <div class="formulario-modelo">
    <!-- Cabecera -->
    <div class="header-flex">
      <div>
        <h1>Resultados de Autoevaluación</h1>
        <p v-if="nombreVersion" class="ctx-modelo">{{ nombreVersion }}</p>
        <p class="subtitulo">
          Ejecución #{{ idEjecucion }}
          <template v-if="datos"> · {{ datos.modelo?.nombre }} · Periodo {{ datos.periodo }} · Escala 0–{{ datos.escala_max }}</template>
        </p>
      </div>
      <button class="btn salir" @click="$router.push({ name: 'ProcesoAutoevaluacionUsuario' })">Volver</button>
    </div>

    <div class="divider"></div>

    <div v-if="store.cargando" class="info">Cargando resultados…</div>

    <template v-else-if="datos">
      <!-- Controles -->
      <div class="controles">
        <div class="tabs">
          <button v-for="e in enfoques" :key="e.id" class="tab" :class="{ activo: enfoque === e.id }" @click="enfoque = e.id">
            {{ e.label }}
          </button>
        </div>
        <div class="acciones-exp">
          <button class="btn ocultar" @click="mostrarPanel = !mostrarPanel">
            {{ mostrarPanel ? 'Cerrar' : 'Mostrar / ocultar' }}
          </button>
          <button
            class="btn mod"
            :disabled="!relacionesCargadas || store.cargando || guardandoCambios"
            @click="modoEdicion ? guardarModificaciones() : (modoEdicion = true)"
          >
            {{ guardandoCambios ? 'Guardando…' : modoEdicion ? 'Guardar' : 'Modificar' }}
          </button>
          <button v-if="modoEdicion" class="btn salir" :disabled="guardandoCambios" @click="cancelarEdicion">
            Cancelar
          </button>
          <button class="btn exp" :disabled="modoEdicion" @click="exportar('csv')">CSV</button>
          <button class="btn exp" :disabled="modoEdicion" @click="exportar('excel')">Excel</button>
          <button class="btn exp" :disabled="modoEdicion" @click="exportar('pdf')">PDF</button>
        </div>
      </div>

      <!-- Panel ocultar/mostrar -->
      <div v-if="mostrarPanel" class="panel-ocultar">
        <p class="panel-titulo">Ocultar factores y características de la hoja</p>
        <div v-for="f in factoresOrdenados" :key="f.id_componente" class="panel-factor">
          <label class="chk">
            <input type="checkbox" :checked="!estaOculto(f.id_componente)" @change="toggle(f.id_componente)" />
            <strong>{{ fmtOrdinal(f.ordinal) }} {{ f.nombre }}</strong>
          </label>
          <div class="panel-caracts">
            <label v-for="c in f.hijos" :key="c.id_componente" class="chk">
              <input type="checkbox" :checked="!estaOculto(c.id_componente)" @change="toggle(c.id_componente)" />
              {{ fmtOrdinal(c.ordinal) }} {{ c.nombre }}
            </label>
          </div>
        </div>
      </div>

      <!-- ═══ HOJA: VISTA GENERAL ═══ -->
      <div v-show="enfoque === 'general'" class="contenedor-tabla" v-drag-scroll>
        <table class="lista-tabla general">
          <thead>
            <tr>
              <th>N°</th>
              <th>Factor</th>
              <th>Ponderación</th>
              <th>N° C.</th>
              <th>Característica</th>
              <th>N° A.</th>
              <th>Aspecto</th>
              <th class="col-num">Valoración</th>
              <th class="col-num">Gradación</th>
              <th class="col-num">% Prom. Ponderado</th>
              <th>Nivel de Cumplimiento</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="fila in filasGenerales" :key="fila.key">
              <tr v-if="fila.tipo === 'aspecto'">
                <td v-if="fila.mostrarFactor" :rowspan="fila.factorRowspan" class="celda-factor">{{ fmtOrdinal(fila.factor.ordinal) }}</td>
                <td v-if="fila.mostrarFactor" :rowspan="fila.factorRowspan" class="celda-factor nombre">{{ fila.factor.nombre }}</td>
                <td v-if="fila.mostrarFactor" :rowspan="fila.factorRowspan" class="celda-factor col-num">{{ fmtPeso(fila.factor.peso) }}</td>
                <template v-if="fila.caract">
                  <td v-if="fila.mostrarCaract" :rowspan="fila.caractRowspan">{{ fmtOrdinal(fila.caract.ordinal) }}</td>
                  <td v-if="fila.mostrarCaract" :rowspan="fila.caractRowspan" class="nombre">{{ fila.caract.nombre }}</td>
                </template>
                <template v-else>
                  <td></td><td class="nombre">—</td>
                </template>
                <td>{{ fmtOrdinal(fila.aspecto?.ordinal) }}</td>
                <td class="nombre">{{ fila.aspecto?.nombre ?? '—' }}</td>
                <td class="col-num">
                  <template v-if="modoEdicion && fila.aspecto && esHistorico(fila.aspecto)">
                    <input
                      type="number"
                      class="input-nota"
                      :min="escalaMinimo"
                      :max="escala"
                      step="0.01"
                      :value="notasEditadas[fila.aspecto.aspectos[0].id_indicador] ?? fila.aspecto.valoracion ?? 0"
                      @change="onCambioNota(fila.aspecto, +($event.target as HTMLInputElement).value)"
                    />
                  </template>
                  <template v-else>{{ fmtVal(fila.aspecto?.valoracion ?? null) }}</template>
                </td>
                <td v-if="fila.caract && fila.mostrarCaract" :rowspan="fila.caractRowspan" class="col-num">{{ fmtPeso(fila.caract.peso) }}</td>
                <td v-else-if="!fila.caract" class="col-num">—</td>
                <td v-if="fila.caract && fila.mostrarCaract" :rowspan="fila.caractRowspan" class="col-num">{{ fmtVal(fila.caract.valoracion) }}</td>
                <td v-else-if="!fila.caract" class="col-num">—</td>
                <td class="nivel" :class="clasClase(fila.aspecto?.clasificacion ?? 'no_aplica')">
                  {{ fila.aspecto?.grado ?? '—' }}
                </td>
              </tr>
              <tr v-else class="fila-subtotal">
                <td colspan="7">SUBTOTAL FACTOR {{ fmtOrdinal(fila.factor.ordinal) }} · {{ fila.factor.nombre }}</td>
                <td class="col-num">{{ fmtVal(fila.factor.valoracion) }}</td>
                <td class="col-num">{{ fmtPeso(fila.factor.peso) }}</td>
                <td class="col-num">{{ fmtPct(fila.factor.pct) }}</td>
                <td class="nivel" :class="clasClase(fila.factor.clasificacion)">{{ fila.factor.grado ?? '—' }}</td>
              </tr>
            </template>
            <tr v-if="datos.global" class="fila-global">
              <td colspan="7">CALIFICACIÓN GLOBAL DEL MODELO</td>
              <td class="col-num">{{ fmtVal(datos.global.valoracion) }}</td>
              <td class="col-num">100%</td>
              <td class="col-num">{{ fmtPct(datos.global.pct) }}</td>
              <td class="nivel" :class="clasClase(datos.global.clasificacion)">{{ datos.global.grado ?? '—' }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- ═══ HOJA: FORTALEZAS Y DEBILIDADES (semáforo) ═══ -->
      <div v-show="enfoque === 'fortalezas'">
        <div class="resumen-cards">
          <div v-for="(r, k) in datos.resumen" :key="k" class="resumen-card">
            <span class="resumen-titulo">{{ nivelLabel(k) }}</span>
            <div class="resumen-fila"><span class="pt fuerte"></span> Fuertes <b>{{ r.fuerte }}</b> <small>({{ pctResumen(r.fuerte, r.total) }})</small></div>
            <div class="resumen-fila"><span class="pt debil"></span> Débiles <b>{{ r.debil }}</b> <small>({{ pctResumen(r.debil, r.total) }})</small></div>
            <div class="resumen-fila"><span class="pt muy_debil"></span> Muy débiles <b>{{ r.muy_debil }}</b> <small>({{ pctResumen(r.muy_debil, r.total) }})</small></div>
            <div class="resumen-fila"><span class="pt no_aplica"></span> No aplica <b>{{ r.no_aplica }}</b></div>
          </div>
        </div>

        <div class="contenedor-tabla" v-drag-scroll>
          <table class="lista-tabla semaforo">
            <thead>
              <tr>
                <th>Característica</th>
                <th>Aspecto</th>
                <th class="col-num">Valoración</th>
                <th class="col-sem">Fuerte</th>
                <th class="col-sem">Débil</th>
                <th class="col-sem">Muy débil</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="grupo in gruposSemaforo" :key="grupo.id">
                <tr class="fila-caract-head">
                  <td colspan="6">{{ fmtOrdinal(grupo.ordinal) }} {{ grupo.nombre }}</td>
                </tr>
                <tr v-for="a in grupo.hojas" :key="grupo.id + '-' + a.id_componente">
                  <td></td>
                  <td class="nombre">{{ (a.ordinal ? fmtOrdinal(a.ordinal) + '. ' : '') + a.nombre }}</td>
                  <td class="col-num">{{ fmtVal(a.valoracion) }}</td>
                  <td class="col-sem"><span v-if="a.clasificacion === 'fuerte'" class="marca fuerte"></span></td>
                  <td class="col-sem"><span v-if="a.clasificacion === 'debil'" class="marca debil"></span></td>
                  <td class="col-sem"><span v-if="a.clasificacion === 'muy_debil'" class="marca muy_debil"></span></td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>

      <!-- ═══ HOJA: ESTRUCTURA COMPLETA ═══ -->
      <div v-show="enfoque === 'estructura'" class="contenedor-tabla" v-drag-scroll>
        <table class="lista-tabla estructura">
          <thead>
            <tr>
              <th>Código</th>
              <th>Tipo</th>
              <th>Nombre</th>
              <th class="col-num">Ponderación</th>
              <th class="col-num">Valoración</th>
              <th class="col-num">%</th>
              <th>Grado</th>
              <th>Clasificación</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="f in filasEstructura" :key="f.key" :class="'nivel-' + f.tipoNivel">
              <td class="cod">{{ fmtOrdinal(f.codigo) }}</td>
              <td>{{ f.tipo }}</td>
              <td class="nombre">{{ f.nombre }}</td>
              <td class="col-num">{{ f.peso === null ? '—' : fmtPeso(f.peso) }}</td>
              <td class="col-num">{{ fmtVal(f.valoracion) }}</td>
              <td class="col-num">{{ fmtPct(f.pct) }}</td>
              <td>{{ f.grado ?? '—' }}</td>
              <td><span class="badge-clas" :class="clasClase(f.clasificacion)">{{ clasLabel(f.clasificacion) }}</span></td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- ═══ HOJA: GRÁFICOS ═══ -->
      <div v-show="enfoque === 'graficos'" class="graficos">
        <div v-for="g in datosGraficos" :key="g.id" class="grafico-card">
          <h3 class="grafico-titulo">Factor {{ fmtOrdinal(g.ordinal) }}: {{ g.nombre }}</h3>
          <svg :viewBox="`0 0 ${g.width} 200`" class="grafico-svg" preserveAspectRatio="xMidYMid meet">
            <!-- eje -->
            <line x1="38" y1="10" x2="38" y2="160" stroke="#ccc" />
            <line x1="38" y1="160" :x2="g.width - 10" y2="160" stroke="#ccc" />
            <template v-for="t in ejeTicks" :key="'t' + t">
              <line x1="34" :y1="yPos(t)" x2="38" :y2="yPos(t)" stroke="#999" />
              <text x="30" :y="yPos(t) + 3" text-anchor="end" class="eje-label">{{ t }}</text>
            </template>
            <!-- barras -->
            <g v-for="(b, i) in g.barras" :key="'b' + i">
              <rect
                :x="48 + i * g.paso" :y="yPos(b.valor ?? 0)"
                :width="g.anchoBarra" :height="160 - yPos(b.valor ?? 0)"
                :fill="clasColor(b.clasificacion)" rx="2"
              />
              <text :x="48 + i * g.paso + g.anchoBarra / 2" :y="yPos(b.valor ?? 0) - 4" text-anchor="middle" class="barra-val">
                {{ b.valor === null ? '—' : b.valor.toFixed(2) }}
              </text>
              <text :x="48 + i * g.paso + g.anchoBarra / 2" y="174" text-anchor="middle" class="barra-label">{{ fmtOrdinal(b.ordinal) }}</text>
            </g>
          </svg>
        </div>
        <p v-if="!datosGraficos.length" class="mensaje-vacio">No hay factores visibles para graficar.</p>
      </div>
    </template>

    <MensajeFlotante
      :visible="mensajeVisible"
      :mensaje="mensajeTexto"
      :tipo="mensajeTipo"
      @cerrar="mensajeVisible = false"
    />
  </div>
</template>

<script setup lang="ts">
/**
 * Informe MAAM de resultados de una ejecución.
 *
 * ── Enrutado ─────────────────────────────────────────────────────
 *   `/ejecucion/:id_ejecucion/resultados` (ResultadosAutoevaluacion) — permiso `calificacion.ver`
 *
 * Vista enrutada. Obtiene sus datos a través de stores Pinia: no llama a `http`
 * ni a los servicios directamente.
 */

import { ref, computed, watch, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useGestionResultadosAutoevaluacion } from '@/store/GestionResultadosAutoevaluacion'
import { useGestionIndicadorInstrumentos } from '@/store/GestionIndicadorInstrumentos'
import { useGestionEvaluacionesIndicadores } from '@/store/GestionEvaluacionesIndicadores'
import useAutenticar from '@/store/IngresoAutenticar'
import type { NodoResultado, Clasificacion } from '@/interfaces/ResultadosAutoevaluacion'
import { exportarCSV, exportarExcel, exportarPDF, type TablaExport, type Celda } from '@/utils/exportUtils'
import MensajeFlotante from '@/components/MensajeFlotante.vue'
import { getProcesoByIdService } from '@/services/ProcesoAutoevaluacionService'
import { getResultadoIndicadorService } from '@/services/ResultadosService'
import { getEvaluacionesIndicadorService } from '@/services/EvaluacionIndicadorService'

const route = useRoute()
const idEjecucion = Number(route.params.id_ejecucion)
const store = useGestionResultadosAutoevaluacion()
const indicadorInstrumentoStore = useGestionIndicadorInstrumentos()
const evaluacionesStore = useGestionEvaluacionesIndicadores()
const authStore = useAutenticar()
const nombreVersion = ref('')
const idVersion = ref<number | null>(null)

// Estado para edición de notas históricas
const idsConInstrumento = ref<Set<number>>(new Set())
const relacionesCargadas = ref(false)
const modoEdicion = ref(false)
const guardandoCambios = ref(false)
const notasEditadas = ref<Record<number, number>>({})

const mensajeVisible = ref(false)
const mensajeTexto   = ref('')
const mensajeTipo    = ref<'positivo' | 'error'>('error')

function mostrarMensaje(texto: string, tipo: 'positivo' | 'error' = 'error') {
  mensajeTexto.value  = texto
  mensajeTipo.value   = tipo
  mensajeVisible.value = true
}

watch(() => store.error, (val) => { if (val) mostrarMensaje(val, 'error') })

const enfoque = ref<'general' | 'fortalezas' | 'estructura' | 'graficos'>('general')
const enfoques = [
  { id: 'general', label: 'Vista general' },
  { id: 'fortalezas', label: 'Fortalezas y debilidades' },
  { id: 'estructura', label: 'Estructura completa' },
  { id: 'graficos', label: 'Gráficos' }
] as const

const mostrarPanel = ref(false)
const ocultos = ref<Set<number>>(new Set())

const datos = computed(() => store.resultados)
const escala = computed(() => datos.value?.escala_max || 5)
const escalaMinimo = computed(() => (datos.value?.escala_max ?? 5) >= 10 ? 1 : 0)

// Ordena ordinales numéricamente: "1.10" > "1.2", "2" > "1"
function cmpOrdinal(a?: string | null, b?: string | null): number {
  const pa = (a ?? '').split('.').map(s => parseInt(s) || 0)
  const pb = (b ?? '').split('.').map(s => parseInt(s) || 0)
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const d = (pa[i] ?? 0) - (pb[i] ?? 0)
    if (d !== 0) return d
  }
  return 0
}

function sortNodo(n: NodoResultado): NodoResultado {
  return {
    ...n,
    hijos: [...n.hijos].sort((a, b) => cmpOrdinal(a.ordinal, b.ordinal)).map(sortNodo)
  }
}

const factoresOrdenados = computed<NodoResultado[]>(() =>
  [...(datos.value?.factores ?? [])]
    .sort((a, b) => cmpOrdinal(a.ordinal, b.ordinal))
    .map(sortNodo)
)

function estaOculto(id: number): boolean {
  return ocultos.value.has(id)
}
function toggle(id: number): void {
  const s = new Set(ocultos.value)
  s.has(id) ? s.delete(id) : s.add(id)
  ocultos.value = s
}

//  Recolectores 

function recolectarHojas(nodo: NodoResultado): NodoResultado[] {
  if (estaOculto(nodo.id_componente)) return []
  if (nodo.es_hoja || !nodo.hijos.length) return [nodo]
  const hojas: NodoResultado[] = []
  for (const h of nodo.hijos) hojas.push(...recolectarHojas(h))
  return hojas
}

const factoresVisibles = computed(() =>
  factoresOrdenados.value.filter(f => !estaOculto(f.id_componente))
)

// Hoja general (con rowspans y subtotales) 

interface FilaGeneral {
  key: string
  tipo: 'aspecto' | 'subtotal'
  factor: NodoResultado
  factorRowspan?: number
  mostrarFactor?: boolean
  caract?: NodoResultado
  caractRowspan?: number
  mostrarCaract?: boolean
  aspecto?: NodoResultado | null
}

const filasGenerales = computed<FilaGeneral[]>(() => {
  const filas: FilaGeneral[] = []
  let n = 0
  for (const factor of factoresVisibles.value) {
    const caracts = (factor.hijos.length ? factor.hijos : [factor]).filter(c => !estaOculto(c.id_componente))
    const grupos = caracts.map(c => {
      const hojas = recolectarHojas(c)
      return { nodo: c, hojas: hojas.length ? hojas : [null] as (NodoResultado | null)[] }
    })
    const totalFactor = grupos.reduce((s, g) => s + g.hojas.length, 0) || 1
    let primeraFactor = true
    for (const g of grupos) {
      let primeraCaract = true
      for (const hoja of g.hojas) {
        filas.push({
          key: `a${n++}`,
          tipo: 'aspecto',
          factor,
          factorRowspan: totalFactor,
          mostrarFactor: primeraFactor,
          caract: g.nodo === factor ? undefined : g.nodo,
          caractRowspan: g.hojas.length,
          mostrarCaract: primeraCaract,
          aspecto: hoja
        })
        primeraFactor = false
        primeraCaract = false
      }
    }
    filas.push({ key: `s${factor.id_componente}`, tipo: 'subtotal', factor })
  }
  return filas
})

// Hoja fortalezas/debilidades (semáforo) 

const gruposSemaforo = computed(() => {
  const grupos: { id: number; ordinal: string | null; nombre: string; hojas: NodoResultado[] }[] = []
  for (const factor of factoresVisibles.value) {
    const caracts = factor.hijos.length ? factor.hijos : [factor]
    for (const c of caracts) {
      if (estaOculto(c.id_componente)) continue
      const hojas = recolectarHojas(c)
      if (hojas.length) grupos.push({ id: c.id_componente, ordinal: c.ordinal, nombre: c.nombre, hojas })
    }
  }
  return grupos
})

//  Hoja estructura completa 

interface FilaEstructura {
  key: string
  codigo: string
  tipo: string
  tipoNivel: number
  sangria: number
  nombre: string
  peso: number | null
  valoracion: number | null
  pct: number | null
  grado: string | null
  clasificacion: Clasificacion
}

const filasEstructura = computed<FilaEstructura[]>(() => {
  const filas: FilaEstructura[] = []
  let n = 0
  const tipoPorNivel = (nivel: number) => (nivel === 1 ? 'Factor' : nivel === 2 ? 'Característica' : 'Subnivel')

  const walk = (nodo: NodoResultado) => {
    if (estaOculto(nodo.id_componente)) return

    // "Aspecto" (conserva la ponderación de la hoja), sin duplicar.
    if (nodo.es_hoja && nodo.aspectos.length <= 1) {
      filas.push({
        key: `e${n++}`,
        codigo: nodo.ordinal ?? '',
        tipo: 'Aspecto',
        tipoNivel: 3,
        sangria: nodo.nivel - 1,
        nombre: nodo.nombre,
        peso: nodo.peso,
        valoracion: nodo.valoracion,
        pct: nodo.pct,
        grado: nodo.grado,
        clasificacion: nodo.clasificacion
      })
      return
    }

    filas.push({
      key: `e${n++}`,
      codigo: nodo.ordinal ?? '',
      tipo: tipoPorNivel(nodo.nivel),
      tipoNivel: Math.min(nodo.nivel, 3),
      sangria: nodo.nivel - 1,
      nombre: nodo.nombre,
      peso: nodo.peso,
      valoracion: nodo.valoracion,
      pct: nodo.pct,
      grado: nodo.grado,
      clasificacion: nodo.clasificacion
    })
    // Hoja con varios aspectos: la hoja agrupa y cada indicador es un aspecto.
    for (const a of nodo.aspectos) {
      filas.push({
        key: `e${n++}`,
        codigo: a.ordinal ?? '',
        tipo: 'Aspecto',
        tipoNivel: 3,
        sangria: nodo.nivel,
        nombre: a.nombre,
        peso: null,
        valoracion: a.valoracion,
        pct: a.pct,
        grado: a.grado,
        clasificacion: a.clasificacion
      })
    }
    for (const h of nodo.hijos) walk(h)
  }
  for (const f of factoresVisibles.value) walk(f)
  return filas
})

//  Gráficos 

const ejeTicks = computed(() => {
  const max = Math.ceil(escala.value)
  const paso = max <= 5 ? 1 : 2
  const ticks: number[] = []
  for (let t = 0; t <= max; t += paso) ticks.push(t)
  return ticks
})
function yPos(v: number): number {
  const max = escala.value || 5
  return 160 - (Math.max(0, Math.min(max, v)) / max) * 150
}

const datosGraficos = computed(() =>
  factoresVisibles.value.map(f => {
    const caracts = (f.hijos.length ? f.hijos : [f]).filter(c => !estaOculto(c.id_componente))
    const barras = caracts.map(c => ({ ordinal: c.ordinal, valor: c.valoracion, clasificacion: c.clasificacion }))
    const paso = 62
    const width = Math.max(240, 60 + barras.length * paso)
    return { id: f.id_componente, ordinal: f.ordinal, nombre: f.nombre, barras, paso, anchoBarra: 38, width }
  }).filter(g => g.barras.length)
)

//  Formato / colores

// Quita el primer segmento del ordinal para la visualización:
// "1.1" → "1", "1.2" → "2", "1.1.1" → "1.1", "1" → "1"
function fmtOrdinal(ordinal?: string | null): string {
  if (!ordinal) return ''
  const parts = ordinal.split('.')
  return parts.length <= 1 ? ordinal : parts.slice(1).join('.')
}

function fmtVal(v: number | null | undefined): string {
  return v === null || v === undefined ? '—' : v.toFixed(2)
}
function fmtPct(v: number | null | undefined): string {
  return v === null || v === undefined ? '—' : `${v.toFixed(1)}%`
}
function fmtPeso(v: number | null | undefined): string {
  return v === null || v === undefined ? '—' : `${(+v).toFixed(v % 1 === 0 ? 0 : 2)}%`
}
function clasClase(c: Clasificacion): string {
  return `clas-${c}`
}
function clasLabel(c: Clasificacion): string {
  return { fuerte: 'Fuerte', debil: 'Débil', muy_debil: 'Muy débil', no_aplica: 'No aplica' }[c]
}
function clasColor(c: Clasificacion): string {
  return { fuerte: '#28a745', debil: '#ffc107', muy_debil: '#dc3545', no_aplica: '#c9c9c9' }[c]
}
function nivelLabel(k: string): string {
  return { factores: 'Factores', caracteristicas: 'Características', aspectos: 'Aspectos' }[k] ?? k
}
function pctResumen(n: number, total: number): string {
  return total > 0 ? `${((n / total) * 100).toFixed(1)}%` : '0%'
}

// Exportación 

function construirTabla(): { tabla: TablaExport; clasFilas: (string | null)[]; merges?: { startRow: number; endRow: number; startCol: number; endCol: number }[] } {
  const d = datos.value!
  const base = `resultados_ejec${idEjecucion}`
  if (enfoque.value === 'general') {
    const headers = ['N°', 'Factor', 'Ponderación', 'N° C.', 'Característica', 'N° A.', 'Aspecto', 'Valoración', 'Gradación', '% Prom. Ponderado', 'Nivel de Cumplimiento']
    const rows: Celda[][] = []
    const clas: (string | null)[] = []
    const merges: { startRow: number; endRow: number; startCol: number; endCol: number }[] = []
    let rowIdx = 0
    for (const fila of filasGenerales.value) {
      if (fila.tipo === 'aspecto') {
        // Registrar rangos de fusión para factor (cols 0-2) y característica (cols 3,4,8,9)
        if (fila.mostrarFactor && (fila.factorRowspan ?? 1) > 1) {
          const span = fila.factorRowspan!
          ;[0, 1, 2].forEach(c => merges.push({ startRow: rowIdx, endRow: rowIdx + span - 1, startCol: c, endCol: c }))
        }
        if (fila.caract && fila.mostrarCaract && (fila.caractRowspan ?? 1) > 1) {
          const span = fila.caractRowspan!
          ;[3, 4, 8, 9].forEach(c => merges.push({ startRow: rowIdx, endRow: rowIdx + span - 1, startCol: c, endCol: c }))
        }
        rows.push([
          fila.mostrarFactor ? fmtOrdinal(fila.factor.ordinal) : null,
          fila.mostrarFactor ? fila.factor.nombre : null,
          fila.mostrarFactor ? fmtPeso(fila.factor.peso) : null,
          fila.caract ? (fila.mostrarCaract ? fmtOrdinal(fila.caract.ordinal) : null) : '',
          fila.caract ? (fila.mostrarCaract ? fila.caract.nombre : null) : '—',
          fmtOrdinal(fila.aspecto?.ordinal),
          fila.aspecto?.nombre ?? '—',
          fmtVal(fila.aspecto?.valoracion ?? null),
          fila.caract ? (fila.mostrarCaract ? fmtPeso(fila.caract.peso) : null) : '—',
          fila.caract ? (fila.mostrarCaract ? fmtVal(fila.caract.valoracion) : null) : '—',
          fila.aspecto?.grado ?? '—'
        ])
        clas.push(fila.aspecto?.clasificacion ?? null)
      } else {
        rows.push([
          `SUBTOTAL FACTOR ${fmtOrdinal(fila.factor.ordinal)} · ${fila.factor.nombre}`,
          '', '', '', '', '', '',
          fmtVal(fila.factor.valoracion),
          fmtPeso(fila.factor.peso),
          fmtPct(fila.factor.pct),
          fila.factor.grado ?? '—'
        ])
        merges.push({ startRow: rowIdx, endRow: rowIdx, startCol: 0, endCol: 6 })
        clas.push('subtotal')
      }
      rowIdx++
    }
    if (d.global) {
      rows.push(['CALIFICACIÓN GLOBAL DEL MODELO', '', '', '', '', '', '', fmtVal(d.global.valoracion), '100%', fmtPct(d.global.pct), d.global.grado ?? '—'])
      merges.push({ startRow: rowIdx, endRow: rowIdx, startCol: 0, endCol: 6 })
      clas.push('global')
    }
    return { tabla: { nombre: `${base}_general`, titulo: 'Resultados de Autoevaluación', headers, rows }, clasFilas: clas, merges }
  }
  if (enfoque.value === 'fortalezas') {
    const headers = ['Característica', 'Aspecto', 'Valoración', 'Clasificación']
    const rows: Celda[][] = []
    const clas: (Clasificacion | null)[] = []
    for (const g of gruposSemaforo.value) {
      for (const a of g.hojas) {
        rows.push([`${g.ordinal ?? ''} ${g.nombre}`, (a.ordinal ? a.ordinal + '. ' : '') + a.nombre, fmtVal(a.valoracion), clasLabel(a.clasificacion)])
        clas.push(a.clasificacion)
      }
    }
    return { tabla: { nombre: `${base}_fortalezas`, titulo: 'Fortalezas y Debilidades', headers, rows }, clasFilas: clas }
  }
  // estructura + graficos → export de la estructura completa
  const headers = ['Código', 'Tipo', 'Nombre', 'Ponderación', 'Valoración', '%', 'Grado', 'Clasificación']
  const rows: Celda[][] = []
  const clas: (Clasificacion | null)[] = []
  for (const f of filasEstructura.value) {
    rows.push([f.codigo, f.tipo, f.nombre, f.peso === null ? '—' : fmtPeso(f.peso), fmtVal(f.valoracion), fmtPct(f.pct), f.grado ?? '—', clasLabel(f.clasificacion)])
    clas.push(f.clasificacion)
  }
  return { tabla: { nombre: `${base}_estructura`, titulo: 'Estructura Completa de Resultados', headers, rows }, clasFilas: clas }
}

function colorFilaExport(clas: (string | null)[]) {
  const map: Record<string, [number, number, number]> = {
    fuerte:    [212, 237, 218],
    debil:     [255, 243, 205],
    muy_debil: [248, 215, 218],
    no_aplica: [240, 240, 240],
    subtotal:  [200, 223, 245],
    global:    [220, 239, 255]
  }
  return (i: number) => {
    const c = clas[i]
    return c && map[c] ? map[c] : null
  }
}

function boldFilaExport(clas: (string | null)[]) {
  return (i: number) => clas[i] === 'subtotal' || clas[i] === 'global'
}

async function exportar(formato: 'csv' | 'excel' | 'pdf'): Promise<void> {
  const { tabla, clasFilas, merges } = construirTabla()
  const subtitulo = datos.value ? `${datos.value.modelo?.nombre ?? ''} · Periodo ${datos.value.periodo} · Escala 0–${datos.value.escala_max}` : undefined
  const colorFila = colorFilaExport(clasFilas)
  const boldFila  = boldFilaExport(clasFilas)
  if (formato === 'csv') exportarCSV(tabla)
  else if (formato === 'excel') await exportarExcel(tabla, { colorFila, boldFila, merges })
  else exportarPDF(tabla, { subtitulo, colorFila, boldFila })
}

// Carga
store.cargar(idEjecucion)

onMounted(async () => {
  // Proceso y relaciones de instrumentos en paralelo para no bloquear la carga
  const [proceso] = await Promise.all([
    getProcesoByIdService(idEjecucion),
    indicadorInstrumentoStore.getRelaciones()
  ])
  nombreVersion.value = (proceso as any)?.version?.nombre ?? ''
  idVersion.value = (proceso as any)?.id_version ?? null
  idsConInstrumento.value = new Set(
    indicadorInstrumentoStore.relaciones.map(r => Number(r.id_indicador))
  )
  relacionesCargadas.value = true
})

// ── Edición de notas históricas ───────────────────────────────────────────

function esHistorico(nodo: NodoResultado | null | undefined): boolean {
  if (!nodo || !nodo.es_hoja || nodo.aspectos.length !== 1) return false
  return !idsConInstrumento.value.has(nodo.aspectos[0].id_indicador)
}

function onCambioNota(nodo: NodoResultado, valor: number) {
  const id = nodo.aspectos[0]?.id_indicador
  if (!id) return
  const min = escalaMinimo.value
  const max = escala.value
  notasEditadas.value = {
    ...notasEditadas.value,
    [id]: Math.min(Math.max(isNaN(valor) ? min : valor, min), max)
  }
}

function cancelarEdicion() {
  notasEditadas.value = {}
  modoEdicion.value = false
}

async function guardarModificaciones() {
  const cambios = Object.entries(notasEditadas.value)
  if (!cambios.length) { modoEdicion.value = false; return }

  const idVer = idVersion.value
  const idUsuario = authStore.usuarioActual?.id
  if (!idVer || !idUsuario) {
    mostrarMensaje('No se puede guardar: datos de sesión incompletos.', 'error')
    return
  }

  guardandoCambios.value = true

  // Obtener registros existentes para conseguir id_evaluacion_indicador (PUT no verifica estado del proceso)
  const registros = await getEvaluacionesIndicadorService({
    id_version: idVer,
    id_ejecucion: idEjecucion,
    id_usuario: Number(idUsuario)
  })

  const pkPorIndicador = new Map<number, number>()
  for (const ev of registros) {
    const idInd = ev.id_indicador
    const pk = (ev as any).id_evaluacion_indicador
    if (idInd && pk) pkPorIndicador.set(Number(idInd), Number(pk))
  }

  const min = escalaMinimo.value
  const max = escala.value

  const resultados = await Promise.all(
    cambios.map(([idInd, nota]) => {
      const pk = pkPorIndicador.get(Number(idInd))
      if (!pk) return Promise.resolve({ ok: false })
      const notaClamped = Math.min(Math.max(nota, min), max)
      return evaluacionesStore.actualizar(pk, {
        calificacion_documental: notaClamped,
        calificacion_cuantitativa: notaClamped,
        calificacion_opinion: notaClamped,
        id_usuario_editor: Number(idUsuario)
      })
    })
  )

  if (resultados.some(r => !r.ok)) {
    guardandoCambios.value = false
    mostrarMensaje('No se pudo guardar los cambios. Intente de nuevo.', 'error')
    return
  }

  // Disparar recalculo de consolidación por cada indicador modificado
  await Promise.all(cambios.map(([idInd]) => getResultadoIndicadorService(idVer, Number(idInd))))

  // Recargar el árbol completo de resultados
  await store.cargar(idEjecucion)

  guardandoCambios.value = false
  notasEditadas.value = {}
  modoEdicion.value = false
  mostrarMensaje('Calificaciones actualizadas correctamente.', 'positivo')
}
</script>

<style scoped>
.formulario-modelo {
  background-color: white; padding: 2rem; border-radius: var(--radius-lg);
  width: 96%; max-width: 1250px; max-height: 100%; overflow-y: auto;
  box-shadow: var(--shadow-md); box-sizing: border-box; margin: 20px auto; font-family: var(--font-family);
}
.header-flex { display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem; }
h1 { font-size: 1.4rem; font-weight: 700; margin: 0; color: var(--text-primary); }
.ctx-modelo { display: inline-block; margin: 6px 0 0; padding: 3px 10px; background: #e8f0f8; border-left: 3px solid #0e69a0; border-radius: 3px; font-size: 0.85rem; font-weight: 700; color: #111; letter-spacing: 0.01em; }
.subtitulo { font-size: 0.85rem; color: #666; margin: 0.25rem 0 0; }
.divider { border: none; border-bottom: 1px solid #e0e0e0; margin: 12px 0 16px; }
.info { padding: 1rem 0; color: #555; }
.aviso { padding: 0.85rem 1rem; background: #fff3cd; color: #856404; border-radius: var(--radius-md); font-size: 0.88rem; }
.aviso-error { background: #f8d7da; color: #721c24; }

.controles { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 1rem; margin-bottom: 1rem; }
.tabs { display: flex; flex-wrap: wrap; gap: 0; border-bottom: 2px solid #e0e0e0; }
.tab { padding: 0.55rem 1.1rem; font-weight: 600; border: none; background: transparent; cursor: pointer; font-family: var(--font-family); font-size: 0.88rem; color: #666; border-bottom: 2px solid transparent; margin-bottom: -2px; transition: all 0.15s ease; }
.tab.activo { color: #0e69a0; border-bottom-color: #0e69a0; }
.tab:hover { color: #0e69a0; }
.acciones-exp { display: flex; gap: 0.5rem; }

.panel-ocultar { border: 1px solid #e0e0e0; border-radius: var(--radius-md); padding: 1rem; margin-bottom: 1rem; background: #fafafa; }
.panel-titulo { font-size: 0.85rem; font-weight: 700; color: #555; margin: 0 0 0.6rem; }
.panel-factor { margin-bottom: 0.6rem; }
.panel-caracts { display: flex; flex-wrap: wrap; gap: 0.25rem 1.25rem; padding-left: 1.5rem; margin-top: 0.2rem; }
.chk { display: flex; align-items: center; gap: 0.4rem; font-size: 0.85rem; cursor: pointer; }

.contenedor-tabla { overflow-x: auto; }
.lista-tabla { width: 100%; border-collapse: collapse; font-size: 0.85rem; }
.lista-tabla th, .lista-tabla td { padding: 8px 10px; text-align: left; border: 1px solid #e0e0e0; vertical-align: middle; }
.lista-tabla thead { background-color: #0e69a0; color: white; text-transform: uppercase; font-size: 0.7rem; letter-spacing: 0.03em; }
.lista-tabla thead th { border-color: #0b587f; }
.col-num { text-align: right; white-space: nowrap; }
.nombre { font-weight: 500; }
.celda-factor { background: #f5f9fc; font-weight: 600; }

.fila-subtotal td { font-weight: 700; font-size: 0.8rem; }
.fila-subtotal td:not(.nivel) { background: #c8dff5; color: #0c3a5e; }
.fila-global td { background: #dcefff; font-weight: 700; border-top: 2px solid #0e69a0; }

.nivel { font-weight: 600; font-size: 0.78rem; }
.clas-fuerte { background: #d4edda; color: #155724; }
.clas-debil { background: #fff3cd; color: #856404; }
.clas-muy_debil { background: #f8d7da; color: #721c24; }
.clas-no_aplica { background: #f0f0f0; color: #888; }

.resumen-cards { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 1rem; margin-bottom: 1.25rem; }
.resumen-card { border: 1px solid #e0e0e0; border-radius: 8px; padding: 0.85rem 1rem; }
.resumen-titulo { font-weight: 700; font-size: 0.9rem; display: block; margin-bottom: 0.5rem; color: #0e69a0; }
.resumen-fila { display: flex; align-items: center; gap: 0.4rem; font-size: 0.82rem; margin-bottom: 0.2rem; }
.resumen-fila small { color: #888; }
.pt { width: 11px; height: 11px; border-radius: 3px; display: inline-block; }
.pt.fuerte, .marca.fuerte { background: #28a745; }
.pt.debil, .marca.debil { background: #ffc107; }
.pt.muy_debil, .marca.muy_debil { background: #dc3545; }
.pt.no_aplica { background: #c9c9c9; }
.semaforo .col-sem { text-align: center; width: 90px; }
.fila-caract-head td { background: #f5f5f5; font-weight: 700; font-size: 0.82rem; }
.marca { width: 16px; height: 16px; border-radius: 4px; display: inline-block; }

.estructura .nivel-1 td { background: #eef5fb; font-weight: 700; }
.estructura .nivel-1 .nombre { color: #0e69a0; }
.estructura .nivel-2 td { background: #f7fbfe; font-weight: 600; }
.estructura .cod { white-space: nowrap; color: #0e69a0; font-weight: 600; font-variant-numeric: tabular-nums; }
.estructura .nombre { font-weight: inherit; }
.badge-clas { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 0.72rem; font-weight: 600; }

.graficos { display: grid; grid-template-columns: repeat(auto-fit, minmax(320px, 1fr)); gap: 1.25rem; }
.grafico-card { border: 1px solid #e0e0e0; border-radius: 8px; padding: 1rem; }
.grafico-titulo { font-size: 0.9rem; font-weight: 700; margin: 0 0 0.5rem; color: #0e69a0; }
.grafico-svg { width: 100%; height: auto; }
.eje-label { font-size: 9px; fill: #888; }
.barra-val { font-size: 9px; font-weight: 700; fill: #333; }
.barra-label { font-size: 9px; fill: #555; }
.mensaje-vacio { color: #888; font-style: italic; padding: 1rem 0; }

.btn { padding: 0.5rem 1rem; font-weight: 600; border: none; border-radius: 8px; cursor: pointer; transition: all 0.15s ease; font-family: var(--font-family); font-size: 0.85rem; }
.btn.salir { background-color: white; border: 1px solid #ccc; color: #333; }
.btn.salir:hover { background-color: #d41a1a; color: white; }
.btn.exp { background-color: #eea222; color: black; }
.btn.exp:hover:not(:disabled) { background-color: #0e69a0; color: white; }
.btn.exp:disabled { opacity: 0.4; cursor: not-allowed; }
.btn.ocultar { background-color: white; border: 1px solid #0e69a0; color: #0e69a0; }
.btn.ocultar:hover { background-color: #0e69a0; color: white; }
.btn.mod { background-color: #0e69a0; color: #fff; }
.btn.mod:hover:not(:disabled) { background-color: #0b5580; }
.btn.mod:disabled { opacity: 0.5; cursor: not-allowed; }

.input-nota {
  width: 70px;
  text-align: center;
  font-size: 0.88rem;
  font-weight: 600;
  padding: 0.2rem 0.3rem;
  border: 2px solid #eea222;
  border-radius: 4px;
  background: #fffdf0;
  font-family: var(--font-family);
  appearance: textfield;
  -moz-appearance: textfield;
}
.input-nota::-webkit-inner-spin-button,
.input-nota::-webkit-outer-spin-button { -webkit-appearance: none; }
.input-nota:focus { outline: none; border-color: #0e69a0; box-shadow: 0 0 0 2px rgba(14,105,160,0.12); }
</style>
